import { Menu, MenuItem, MenuTrigger } from '@affine/component';
import clsx from 'clsx';
import { useId } from 'react';

import * as styles from './settings-select.css';

export interface SettingsSelectOption<T extends string> {
  value: T;
  label: string;
  /** A second line under the label in the open list. */
  hint?: string;
  disabled?: boolean;
  /** Marks the option in the open list, for tests that pick it. */
  testId?: string;
}

/** A dropdown for one of a few named values, for settings rows and forms. */
export function SettingsSelect<T extends string>({
  value,
  options,
  onChange,
  placeholder,
  disabled,
  labelledBy,
  align = 'start',
  className,
  menuClassName,
  testId,
  'aria-label': ariaLabel,
}: {
  value: T;
  options: ReadonlyArray<SettingsSelectOption<T>>;
  onChange: (value: T) => void;
  /** Shown while no option carries the value, e.g. when nothing can be chosen. */
  placeholder?: string;
  disabled?: boolean;
  /** Id of the visible label; the control is then read as "label, value". */
  labelledBy?: string;
  align?: 'start' | 'end';
  className?: string;
  menuClassName?: string;
  testId?: string;
  'aria-label'?: string;
}) {
  const id = useId();
  const current = options.find(option => option.value === value);
  return (
    <Menu
      contentOptions={{ align, className: menuClassName }}
      items={options.map(option => (
        <MenuItem
          key={option.value}
          selected={option.value === value}
          disabled={option.disabled}
          data-testid={option.testId}
          onSelect={() => {
            if (!option.disabled) onChange(option.value);
          }}
        >
          {option.hint === undefined ? (
            option.label
          ) : (
            <span className={styles.option}>
              <span>{option.label}</span>
              <span className={styles.optionHint}>{option.hint}</span>
            </span>
          )}
        </MenuItem>
      ))}
    >
      <MenuTrigger
        block
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy ? `${labelledBy} ${id}` : undefined}
        className={clsx(styles.trigger, className)}
        disabled={disabled || options.every(option => option.disabled)}
        data-testid={testId}
      >
        {current?.label ?? placeholder ?? ''}
      </MenuTrigger>
    </Menu>
  );
}
